import { FlatList, Image, StyleSheet, View, type ListRenderItemInfo } from 'react-native';
import { AlertCircle, Brain, MessageCircle, RefreshCw, Send } from 'lucide-react-native';
import type {
  PhotoSearchConversationViewModel,
  PhotoSearchMessageViewModel,
  PhotoSearchSectionItemViewModel,
  PhotoSearchSectionViewModel,
} from '../../types/photoSearch';
import { tokens } from '../../theme/tokens';
import { AppButton } from '../ui/AppButton';
import { AppCard } from '../ui/AppCard';
import { AppInput } from '../ui/AppInput';
import { AppText } from '../ui/AppText';
import { KnowledgeUpdateSummary } from './KnowledgeUpdateSummary';
import { PhotoSearchResultActions } from './PhotoSearchResultActions';
import { PhotoSearchStageProgress } from './PhotoSearchStageProgress';

const quickReplies = ['再讲慢一点', '给我一道同类题', '直接看答案'];
const explainModeOptions = [
  { label: '快讲', prompt: '请用快讲模式总结这道题的核心解法' },
  { label: '深讲', prompt: '请用深讲模式逐步拆解这道题' },
];

type PhotoSearchConversationProps = {
  conversation: PhotoSearchConversationViewModel;
  followUpText: string;
  isSubmitting: boolean;
  onFollowUpTextChange: (text: string) => void;
  onSendFollowUp: (text: string) => void;
  onRetry: () => void;
  onReset: () => void;
  savedToWrongBook: boolean;
  savingToWrongBook: boolean;
  onMarkHelpful: () => void;
  onAddToWrongBook: () => void;
  onReportAnswerIssue: () => void;
  onOpenDiagram: () => void;
};

export function PhotoSearchConversation({
  conversation,
  followUpText,
  isSubmitting,
  onFollowUpTextChange,
  onSendFollowUp,
  onRetry,
  onReset,
  savedToWrongBook,
  savingToWrongBook,
  onMarkHelpful,
  onAddToWrongBook,
  onReportAnswerIssue,
  onOpenDiagram,
}: PhotoSearchConversationProps) {
  const busy = isSubmitting || conversation.loading;
  const canSend = Boolean(conversation.canContinue && followUpText.trim() && !busy);

  const renderMessage = ({ item: message }: ListRenderItemInfo<PhotoSearchMessageViewModel>) => (
    <MessageCard
      message={message}
      savedToWrongBook={savedToWrongBook}
      savingToWrongBook={savingToWrongBook}
      onMarkHelpful={onMarkHelpful}
      onAddToWrongBook={onAddToWrongBook}
      onReportAnswerIssue={onReportAnswerIssue}
      onRetry={onRetry}
    />
  );

  return (
    <View style={styles.wrapper}>
      <AppCard style={styles.statusCard}>
        <View style={styles.statusTopRow}>
          <View style={styles.statusCopy}>
            <AppText variant="subtitle" weight="bold">
              AI 搜题讲解
            </AppText>
            <AppText variant="caption" tone="muted" numberOfLines={1}>
              会话 ID：{conversation.sessionId}
            </AppText>
          </View>
          <View style={styles.statusPill}>
            <AppText variant="caption" tone="primary" weight="bold">
              {conversation.currentStage}
            </AppText>
          </View>
        </View>

        <View style={styles.questionStrip}>
          <Image source={{ uri: conversation.image.uri }} style={styles.questionThumb} />
          <View style={styles.statusCopy}>
            <AppText variant="body" weight="bold">
              题图已上传
            </AppText>
            <AppText variant="caption" tone="muted" numberOfLines={1}>
              {conversation.imageRef}
            </AppText>
          </View>
        </View>

        <PhotoSearchStageProgress
          currentStageKey={conversation.currentStageKey}
          streamStatus={conversation.streamStatus}
        />

        <View style={styles.explainModeRow}>
          {explainModeOptions.map((option) => (
            <AppButton
              key={option.label}
              label={option.label}
              variant="secondary"
              disabled={busy || !conversation.canContinue}
              onPress={() => onSendFollowUp(option.prompt)}
              style={styles.explainModeButton}
            />
          ))}
        </View>
      </AppCard>

      <FlatList
        data={conversation.messages}
        keyExtractor={(message) => message.id}
        renderItem={renderMessage}
        scrollEnabled={false}
        ItemSeparatorComponent={MessageSeparator}
      />

      <AppCard style={styles.followCard}>
        <View style={styles.followTitleRow}>
          <MessageCircle size={18} color={tokens.colors.primary} />
          <View style={styles.statusCopy}>
            <AppText variant="body" weight="bold">
              继续追问
            </AppText>
            <AppText variant="caption" tone="muted">
              本轮讲解完成后，可以围绕这道题继续问。
            </AppText>
          </View>
        </View>

        <View style={styles.quickReplyRow}>
          {quickReplies.map((reply) => (
            <AppButton
              key={reply}
              label={reply}
              variant="secondary"
              disabled={busy || !conversation.canContinue}
              onPress={() => onSendFollowUp(reply)}
              style={styles.quickReply}
            />
          ))}
        </View>

        <View style={styles.inputRow}>
          <AppInput
            value={followUpText}
            onChangeText={onFollowUpTextChange}
            placeholder="例如：第二步为什么能这样变形？"
            multiline
            style={styles.followInput}
          />
          <AppButton
            label=""
            disabled={!canSend}
            onPress={() => onSendFollowUp(followUpText)}
            leftSlot={<Send size={18} color={tokens.colors.white} />}
            style={styles.sendButton}
          />
        </View>

        <View style={styles.actionRow}>
          <AppButton
            label="重新拍照"
            variant="secondary"
            onPress={onReset}
            leftSlot={<RefreshCw size={16} color={tokens.colors.primary} />}
            style={styles.actionButton}
          />
          <AppButton
            label="重新生成"
            variant="secondary"
            onPress={onRetry}
            disabled={busy}
            leftSlot={<Brain size={16} color={tokens.colors.primary} />}
            style={styles.actionButton}
          />
        </View>
        <AppButton
          label="图像交互"
          variant="secondary"
          onPress={onOpenDiagram}
          disabled={busy}
        />
      </AppCard>
    </View>
  );
}

type MessageCardProps = {
  message: PhotoSearchMessageViewModel;
  savedToWrongBook: boolean;
  savingToWrongBook: boolean;
  onMarkHelpful: () => void;
  onAddToWrongBook: () => void;
  onReportAnswerIssue: () => void;
  onRetry: () => void;
};

function MessageCard({
  message,
  savedToWrongBook,
  savingToWrongBook,
  onMarkHelpful,
  onAddToWrongBook,
  onReportAnswerIssue,
  onRetry,
}: MessageCardProps) {
  if (message.kind === 'user_image') {
    return (
      <AppCard style={styles.userImageCard}>
        <View style={styles.statusCopy}>
          <AppText variant="body" weight="bold">
            我上传的题目
          </AppText>
          <AppText variant="caption" tone="muted">
            题图已交给 AI 处理
          </AppText>
        </View>
        <Image source={{ uri: message.imageUri }} style={styles.userImageThumb} />
      </AppCard>
    );
  }

  if (message.kind === 'user_text') {
    return (
      <View style={styles.userBubble}>
        <AppText variant="body" style={styles.userBubbleText}>
          {message.content}
        </AppText>
      </View>
    );
  }

  const isError = message.kind === 'assistant_error';

  return (
    <AppCard style={[styles.assistantCard, isError ? styles.assistantErrorCard : null]}>
      <View style={styles.assistantHeader}>
        {isError ? (
          <AlertCircle size={18} color={tokens.colors.danger} />
        ) : (
          <Brain size={18} color={tokens.colors.primary} />
        )}
        <AppText variant="body" weight="bold" tone={isError ? 'danger' : 'default'}>
          {isError ? 'AI 讲解中断' : message.kind === 'assistant_final' ? 'AI 解题讲解' : 'AI 正在讲解'}
        </AppText>
      </View>

      {message.stage ? (
        <AppText variant="caption" tone="primary" weight="semibold">
          {message.stage}
        </AppText>
      ) : null}

      {message.content ? (
        <AppText variant="body" tone={isError ? 'danger' : 'default'} style={styles.messageText}>
          {message.content}
        </AppText>
      ) : (
        <AppText variant="body" tone="muted" style={styles.messageText}>
          正在组织讲解...
        </AppText>
      )}

      <SectionList sections={message.sections} />
      <AnswerPacket message={message} />

      {message.safety.status !== 'normal' ? (
        <View style={styles.safetyNotice}>
          <AppText
            variant="caption"
            tone={message.safety.status === 'blocked' ? 'danger' : 'warning'}
            weight="bold"
          >
            {message.safety.message}
          </AppText>
        </View>
      ) : null}

      <KnowledgeUpdateSummary impacts={message.knowledgeImpacts} />

      {message.kind === 'assistant_final' ? (
        <PhotoSearchResultActions
          savedToWrongBook={savedToWrongBook}
          savingToWrongBook={savingToWrongBook}
          onMarkHelpful={onMarkHelpful}
          onAddToWrongBook={onAddToWrongBook}
          onReportAnswerIssue={onReportAnswerIssue}
          onRetry={onRetry}
        />
      ) : null}
    </AppCard>
  );
}

function SectionList({ sections }: { sections: PhotoSearchSectionViewModel[] }) {
  if (!sections.length) {
    return null;
  }

  return (
    <View style={styles.sectionsWrap}>
      {sections.map((section) => (
        <View key={section.id} style={styles.sectionBlock}>
          <AppText variant="body" weight="bold">
            {section.title}
          </AppText>
          {section.items.map((sectionItem) => (
            <SectionItem key={sectionItem.id} sectionItem={sectionItem} />
          ))}
        </View>
      ))}
    </View>
  );
}

function AnswerPacket({ message }: { message: PhotoSearchMessageViewModel }) {
  if (message.role !== 'assistant' || !message.answerPacket) {
    return null;
  }

  return (
    <View style={styles.answerPacket}>
      {message.answerPacket.brief ? (
        <AppText variant="body" weight="semibold">
          {message.answerPacket.brief}
        </AppText>
      ) : null}

      {message.answerPacket.steps.map((step) => (
        <SectionItem key={step.id} sectionItem={step} />
      ))}

      {message.answerPacket.finalAnswer ? (
        <View style={styles.finalAnswerBox}>
          <AppText variant="caption" tone="primary" weight="bold">
            参考答案
          </AppText>
          <AppText variant="body" weight="bold">
            {message.answerPacket.finalAnswer}
          </AppText>
        </View>
      ) : null}
    </View>
  );
}

function SectionItem({ sectionItem }: { sectionItem: PhotoSearchSectionItemViewModel }) {
  return (
    <View style={styles.sectionItemRow}>
      {sectionItem.step ? (
        <View style={styles.stepBadge}>
          <AppText variant="caption" style={styles.stepBadgeText} weight="bold">
            {sectionItem.step}
          </AppText>
        </View>
      ) : null}
      <View style={styles.sectionItemCopy}>
        {sectionItem.label ? (
          <AppText variant="caption" tone="muted" weight="semibold">
            {sectionItem.label}
          </AppText>
        ) : null}
        <AppText variant="caption">{sectionItem.value}</AppText>
      </View>
    </View>
  );
}

function MessageSeparator() {
  return <View style={styles.messageSeparator} />;
}

const styles = StyleSheet.create({
  wrapper: {
    gap: tokens.spacing.lg,
  },
  statusCard: {
    gap: tokens.spacing.lg,
  },
  statusTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
  },
  statusCopy: {
    flex: 1,
  },
  statusPill: {
    minHeight: 32,
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  questionStrip: {
    borderRadius: tokens.radius.lg,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    padding: tokens.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    backgroundColor: tokens.colors.background,
  },
  explainModeRow: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
  },
  explainModeButton: {
    flex: 1,
  },
  questionThumb: {
    width: 58,
    height: 58,
    borderRadius: tokens.radius.md,
  },
  userImageCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  userImageThumb: {
    width: 72,
    height: 72,
    borderRadius: tokens.radius.md,
  },
  userBubble: {
    alignSelf: 'flex-end',
    maxWidth: '86%',
    borderRadius: tokens.radius.lg,
    borderTopRightRadius: tokens.radius.sm,
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    backgroundColor: tokens.colors.primary,
  },
  userBubbleText: {
    color: tokens.colors.white,
  },
  assistantCard: {
    gap: tokens.spacing.sm,
  },
  assistantErrorCard: {
    backgroundColor: tokens.colors.dangerSoft,
    borderColor: tokens.colors.danger,
  },
  assistantHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  messageText: {
    marginTop: tokens.spacing.xs,
  },
  sectionsWrap: {
    gap: tokens.spacing.sm,
    marginTop: tokens.spacing.sm,
  },
  sectionBlock: {
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
    gap: tokens.spacing.sm,
    backgroundColor: tokens.colors.background,
  },
  sectionItemRow: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
    alignItems: 'flex-start',
  },
  stepBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.primary,
  },
  stepBadgeText: {
    color: tokens.colors.white,
  },
  sectionItemCopy: {
    flex: 1,
  },
  answerPacket: {
    gap: tokens.spacing.sm,
    marginTop: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  finalAnswerBox: {
    gap: tokens.spacing.xs,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.primary,
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.surface,
  },
  safetyNotice: {
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.warningSoft,
  },
  followCard: {
    gap: tokens.spacing.md,
  },
  followTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  quickReplyRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  quickReply: {
    minHeight: 36,
    paddingHorizontal: tokens.spacing.md,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: tokens.spacing.sm,
  },
  followInput: {
    flex: 1,
    minHeight: 54,
    maxHeight: 96,
    paddingVertical: tokens.spacing.sm,
  },
  sendButton: {
    width: 48,
    minHeight: 48,
    paddingHorizontal: 0,
  },
  actionRow: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
  },
  actionButton: {
    flex: 1,
  },
  messageSeparator: {
    height: tokens.spacing.md,
  },
});
